<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import UiTag from '@/views/CRM/components/UiTag.vue';
import type { TagSize } from '@/types/core';
//#endregion

//#region types
type TagPreset =
  | 'default'
  | 'contrast'
  | 'gray'
  | 'red'
  | 'notify-email'
  | 'notify-sms'
  | 'notify-call'
  | 'notify-push';

interface PresetData {
  text?: string;
  icon?: string;
  type?: string;
}

interface Props {
  icon?: string;
  lib?: string;
  size?: TagSize;
  preset?: TagPreset;
  clickable?: boolean | '';
}

interface Emits {
  click: [event: PointerEvent];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  preset: 'default',
  clickable: false,
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const { t } = useI18n();
//#endregion

//#region computed
const presetList = computed<Record<TagPreset, PresetData>>(() => ({
  default: {},
  contrast: {
    type: 'contrast',
  },
  gray: {
    type: 'gray',
  },
  red: {
    type: 'red',
  },
  'notify-email': {
    text: 'Mail',
    icon: 'envelope',
    type: 'notify-email',
  },
  'notify-sms': {
    text: 'SMS',
    icon: 'comment-sms',
    type: 'notify-sms',
  },
  'notify-call': {
    text: t('crm.fields.actions.values.call_center'),
    icon: 'phone',
    type: 'notify-call',
  },
  'notify-push': {
    text: t('crm.fields.actions.values.push'),
    icon: 'bell',
    type: 'notify-push',
  },
}));

const pPreset = computed<PresetData>(() => presetList.value[props.preset] || presetList.value.default);

const pIcon = computed(() => props.icon || pPreset.value.icon);
const pText = computed(() => pPreset.value.text);

const computedClasses = computed<Record<string, boolean>>(() => {
  const result: Record<string, boolean> = {
    'crm-tag_clickable': Boolean(props.clickable) || props.clickable === '',
  };

  if (pPreset.value.type) {
    result[`crm-tag_type-${pPreset.value.type}`] = true;
  }

  return result;
});
//#endregion
</script>

<template>
  <UiTag
    class="crm-tag"
    :class="computedClasses"
    :icon="pIcon"
    :lib="lib"
    :size="size"
    @click="emit('click', $event)"
  >
    <template v-slot:prepend>
      <slot name="prepend" />
    </template>
    <slot>{{ pText }}</slot>
  </UiTag>
</template>


<style lang="scss">
.crm-tag {
  height: 20px;
  &_type-contrast {
    //border-radius: 8px;
    //line-height: 14px;
    border: solid 1px var(--palette-main-primary);
    background-color: #ffffff;
    color: var(--palette-main-primary);
  }

  &_type-gray {
    background-color: #eaeaea;
    color: #303634;
  }

  &_type-red {
    //border-radius: 8px;
    //line-height: 14px;
    border: solid 1px var(--text-negative);
    background-color: #ffffff;
    color: var(--text-negative);
  }

  &_type-notify-sms {
    background-color: var(--crm-sms);
    color: #ffffff;
  }

  @at-root #{selector-append(& + '_type-notify-sms', & + '_clickable')} {
    &:hover {
      background-color: color-mix(in srgb, var(--crm-sms) 90%, black);
      //background-color: darken(var(--crm-sms), 10%);
    }
  }

  &_type-notify-call {
    background-color: var(--crm-call);
    color: #ffffff;
  }

  @at-root #{selector-append(& + '_type-notify-call', & + '_clickable')} {
    &:hover {
      background-color: color-mix(in srgb, var(--crm-call) 90%, black);
    }
  }

  &_type-notify-email {
    background-color: var(--crm-email);
    color: #ffffff;
  }

  @at-root #{selector-append(& + '_type-notify-email', & + '_clickable')} {
    &:hover {
      background-color: color-mix(in srgb, var(--crm-email) 90%, black);
    }
  }

  &_type-notify-push {
    background-color: var(--crm-push);
    color: #ffffff;
  }

  @at-root #{selector-append(& + '_type-notify-push', & + '_clickable')} {
    &:hover {
      background-color: color-mix(in srgb, var(--crm-push) 90%, black);
    }
  }

  &_clickable {
    cursor: pointer;
  }
}
</style>
